import _ from 'lodash';
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMutation } from '@apollo/client';
import { StripePaymentMethods, ErrorHandler } from '@playbookux/playbook-web-core';

import { config } from '../../../../../config';

import {
  VERIFY_CAPTCHA,
} from '../../graphql';

import {
  Button,
  Content,
  Header,
  Input,
  InputContainer,
  Modal,
  StripeContainer,
  Text,
  ContainerInputError,
  ErrorMsg,
} from './styles';

const HEADER = 'Add prepaid balance';
const INPUT_MESSAGE = 'How much would you like to add to your account?';

const schema = yup
  .number()
  .integer('Prepaid Balance must be an integer')
  .positive('Enter a valid number')
  .typeError('Enter a valid number')
  .required('Required');

const PrepaidBalanceModal = (props) => {
  const {
    callback,
    error,
    onClose,
    onSubmit,
    open,
    paymentMethods,
    loading,
  } = props;

  const defaultPaymentMethod = useMemo(() => {
    if (_.isEmpty(paymentMethods)) return null;

    const { id } = _.find(paymentMethods, 'isDefault') || _.first(paymentMethods);

    return id;
  }, [paymentMethods]);

  const isRecaptchaEnabled = config.REACT_APP_RECAPTCHA_ENABLED === 'true';
  const recaptchaRef = useRef();

  const [prepaidValue, setPrepaidValue] = useState();
  const [inputError, setInputError] = useState();
  const [selectedCard, setSelectedCard] = useState(defaultPaymentMethod);
  const [rememberCard, setRememberCard] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(!isRecaptchaEnabled);

  const [verifyCaptcha] = useMutation(VERIFY_CAPTCHA);

  useEffect(() => {
    if (!open) {
      setPrepaidValue(null);
    }
  }, [open]);

  const handleChange = (e, { value }) => {
    setInputError(null);
    setPrepaidValue(value);

    schema.validate(value)
      .catch((err) => {
        setInputError(err.message);
      });
  };

  const resetStates = () => {
    setIsCaptchaVerified(false);
    setInputError(undefined);
    setPrepaidValue(undefined);
  };

  const handleSubmit = () => {
    if (inputError) return;
    if (!isCaptchaVerified) {
      setInputError('Something wrong with card or captcha, try again');
      return;
    }

    schema.validate(prepaidValue)
      .catch((err) => {
        setInputError(err.message);
      });

    const prepaidAmount = parseInt(prepaidValue, 10) * 100;
    setIsCaptchaVerified(isRecaptchaEnabled || false);
    resetStates();
    onSubmit(prepaidAmount, rememberCard, selectedCard);
  };

  const handleCaptchaVerify = async (responsetoken) => {
    try {
      const { data } = await verifyCaptcha({
        variables: {
          token: responsetoken,
        },
      });

      const { verifyCaptcha: { success } } = data;

      if (!success) {
        setInputError('Something wrong with captcha, try again');
        throw new Error('Something wrong with captcha, try again');
      }
      setIsCaptchaVerified(true);
    } catch (e) {
      setInputError('Something wrong with captcha, try again');
      recaptchaRef.current.reset();
    }
  };

  const handleClose = () => {
    resetStates();
    onClose(!open);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
    >
      <Content>
        <Header as="h3">{HEADER}</Header>

        <InputContainer>
          <Text withContainer>{INPUT_MESSAGE}</Text>
          <Text noMargin>{' $ '}</Text>
          <ContainerInputError>
            <Input
              onChange={handleChange}
              value={prepaidValue}
              error={inputError}
            />
            {inputError && (
              <ErrorMsg>Enter a valid number</ErrorMsg>
            )}
          </ContainerInputError>
        </InputContainer>

        <StripeContainer>
          <StripePaymentMethods
            callback={callback}
            onSetRememberCard={setRememberCard}
            onSetSelectedCard={setSelectedCard}
            paymentMethods={paymentMethods}
            rememberCard={rememberCard}
            selectedCard={selectedCard}
          />
        </StripeContainer>

        <Text>
          The prepaid balance will be added to your account immediately.
          In the future, you won’t have to add a credit card when submitting a project.
        </Text>

        <Text>
          The prepaid balance expires in 12 months. If you plan to spend over $10,000 USD, contact sales for enterprise pricing.
        </Text>

        {error && (
          <ErrorHandler
            error={error}
            defaultMessage="Error while adding prepaid balance."
          />
        )}

        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={config.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_V2}
          onChange={handleCaptchaVerify}
        />

        <Button
          disabled={!isCaptchaVerified || !prepaidValue || inputError}
          loading={loading}
          onClick={handleSubmit}
        >
          Submit & Pay
        </Button>
      </Content>
    </Modal>
  );
};

PrepaidBalanceModal.propTypes = {
  loading: PropTypes.bool,
  callback: PropTypes.func.isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.object).isRequired,
};

PrepaidBalanceModal.defaultProps = {
  error: undefined,
  loading: false,
};

export default PrepaidBalanceModal;
